import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, fmtMoney, downloadWithToast, formatApiErrorDetail } from "@/lib/apiClient";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { FilePdf, FileZip, FloppyDisk, Eye, LockSimple, LockSimpleOpen, MagnifyingGlass, CalendarCheck } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import SlipPreviewDialog from "@/components/hr/SlipPreviewDialog";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const periodOf = (r) => `${MONTHS[(r.month || 1) - 1]} ${r.year}`;
const NUM_FIELDS = ["days_paid", "basic", "hra", "conv", "other_earnings", "pf_wages", "pf_employee", "esi_wages", "esi_employee", "other_deductions", "attendance_deduction"];
const n = (v) => Number(v || 0);
const derive = (r) => {
  const gross = n(r.basic) + n(r.hra) + n(r.conv) + n(r.other_earnings);
  const ded = n(r.pf_employee) + n(r.esi_employee) + n(r.other_deductions) + n(r.attendance_deduction);
  return { gross, ded, net: gross - ded };
};

export default function PayrollRunSheet({ runId, onClose, onChanged }) {
  const [run, setRun] = useState(null);
  const [rows, setRows] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState("");
  const [preview, setPreview] = useState(null);
  const [prorate, setProrate] = useState(true);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/payroll/runs/${runId}`);
      setRun(data);
      setRows(data.rows || []);
      setDirty(false);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  }, [runId]);

  useEffect(() => { load(); }, [load]);

  const locked = run?.status === "finalised";

  const setField = (idx, key, value) => {
    setRows((prev) => prev.map((r, i) => {
      if (i !== idx) return r;
      const next = { ...r, [key]: NUM_FIELDS.includes(key) ? (value === "" ? "" : Number(value)) : value };
      if (key === "days_paid" && prorate && run?.days_in_month) {
        const f = Math.max(0, Math.min(1, n(value) / run.days_in_month));
        next.basic = Math.round(n(r.fixed_basic) * f);
        next.hra = Math.round(n(r.fixed_hra) * f);
        next.conv = Math.round(n(r.fixed_conv) * f);
      }
      return next;
    }));
    setDirty(true);
  };

  const save = async (extra = {}) => {
    setSaving(true);
    try {
      const payload = { rows: rows.map((r) => ({ ...r, ...Object.fromEntries(NUM_FIELDS.map((k) => [k, n(r[k])])) })), ...extra };
      const { data } = await api.put(`/payroll/runs/${runId}`, payload);
      setRun(data); setRows(data.rows || []); setDirty(false);
      toast.success(extra.status ? `Run ${extra.status === "finalised" ? "finalised & locked" : "re-opened"}` : "Payroll run saved");
      onChanged?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setSaving(false); }
  };

  const syncAttendance = async () => {
    if (!window.confirm("Sync attendance into this run? Paid days, leaves, late marks, half-days and the Attendance Deduction will be overwritten from the Attendance Register; earnings reset to the full monthly rate.")) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/payroll/attendance/${run.year}/${run.month}/apply-to-run`);
      toast.success(`Synced ${data.updated} employees from attendance${data.skipped?.length ? ` · ${data.skipped.length} without attendance skipped` : ""}`);
      await load();
      onChanged?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setSaving(false); }
  };

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    return rows.map((r, idx) => ({ r, idx })).filter(({ r }) => !t || [r.name, r.emp_code, r.uan, r.father_name, r.designation].some((x) => (x || "").toLowerCase().includes(t)));
  }, [rows, q]);

  const totals = useMemo(() => rows.reduce((a, r) => { const d = derive(r); a.gross += d.gross; a.ded += d.ded; a.net += d.net; return a; }, { gross: 0, ded: 0, net: 0 }), [rows]);

  if (!run) return null;
  const period = periodOf(run);
  const slipName = (r) => `SalarySlip_${(r.name || "Employee").replace(/\s+/g, "_")}_${period.replace(" ", "-")}.pdf`;

  return (
    <Sheet open onOpenChange={(v) => !v && onClose?.()}>
      <SheetContent className="w-[min(1480px,100vw)] sm:max-w-none overflow-y-auto" data-testid="payroll-run-sheet">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-3 flex-wrap">
            <span className="font-display">Payroll · {period}</span>
            <span className={`px-2 py-0.5 rounded-sm text-[9.5px] font-semibold uppercase tracking-widest ${locked ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`} data-testid="payroll-run-status">{run.status}</span>
            <span className="text-[11px] font-normal text-slate-500">Form No. {run.form_no} · {run.source_filename}</span>
          </SheetTitle>
        </SheetHeader>

        <div className="mt-3 grid grid-cols-2 md:grid-cols-6 gap-2 text-[11.5px]">
          <Meta label="Days in month">
            <Input type="number" min={1} max={31} value={run.days_in_month} disabled={locked} className="h-7 text-[11.5px] w-20"
                   onChange={(e) => { setRun({ ...run, days_in_month: Number(e.target.value) }); setDirty(true); }} data-testid="payroll-days-input" />
          </Meta>
          <Meta label="Pay date (on slip)">
            <Input type="date" value={run.pay_date || ""} disabled={locked} className="h-7 text-[11.5px]"
                   onChange={(e) => { setRun({ ...run, pay_date: e.target.value }); setDirty(true); }} data-testid="payroll-paydate-input" />
          </Meta>
          <Meta label="Gross"><div className="font-mono-num font-semibold text-slate-900">{fmtMoney(totals.gross)}</div></Meta>
          <Meta label="Deductions"><div className="font-mono-num font-semibold text-rose-700">{fmtMoney(totals.ded)}</div></Meta>
          <Meta label="Net payable"><div className="font-mono-num font-bold text-emerald-700" data-testid="payroll-run-net">{fmtMoney(totals.net)}</div></Meta>
          <Meta label="Employees"><div className="font-mono-num font-semibold">{rows.length} <span className="text-slate-400 font-normal">· PF {run.totals?.pf_members} · ESI {run.totals?.esi_members}</span></div></Meta>
        </div>

        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-sm px-2 py-1 flex-1 min-w-[220px]">
            <MagnifyingGlass size={13} className="text-slate-400" />
            <Input placeholder="Search employee, code, UAN…" value={q} onChange={(e) => setQ(e.target.value)} className="h-7 text-[11.5px] border-0 shadow-none focus-visible:ring-0" data-testid="payroll-row-search" />
          </div>
          <label className="flex items-center gap-1.5 text-[11px] text-slate-600 select-none">
            <input type="checkbox" checked={prorate} onChange={(e) => setProrate(e.target.checked)} data-testid="payroll-prorate-toggle" />
            Auto-prorate earnings when days change
          </label>
          <Button size="sm" variant="outline" className="h-8 text-[11.5px] text-sky-700 border-sky-200" onClick={syncAttendance} disabled={locked || saving} title="Pull paid days, leaves, late marks & attendance deduction from the Attendance Register" data-testid="payroll-sync-attendance">
            <CalendarCheck size={12} className="mr-1" /> Sync from Attendance
          </Button>
          <Button size="sm" variant="ghost" className="h-8 text-[11.5px] text-slate-500" onClick={() => navigate("/payroll/attendance")} data-testid="payroll-open-attendance">Open register →</Button>
          <Button size="sm" variant="outline" className="h-8 text-[11.5px]" onClick={() => downloadWithToast(`/payroll/runs/${runId}/slips.pdf`, `SalarySlips_All_${period.replace(" ", "-")}.pdf`).catch(() => {})} data-testid="payroll-sheet-all-pdf">
            <FilePdf size={12} className="mr-1" /> All Slips PDF
          </Button>
          <Button size="sm" variant="outline" className="h-8 text-[11.5px]" onClick={() => downloadWithToast(`/payroll/runs/${runId}/slips.zip`, `SalarySlips_${period.replace(" ", "-")}.zip`).catch(() => {})} data-testid="payroll-sheet-zip">
            <FileZip size={12} className="mr-1" /> ZIP
          </Button>
          {locked ? (
            <Button size="sm" variant="outline" className="h-8 text-[11.5px] text-amber-700 border-amber-200" onClick={() => save({ status: "draft" })} disabled={saving} data-testid="payroll-reopen-btn">
              <LockSimpleOpen size={12} className="mr-1" /> Re-open
            </Button>
          ) : (
            <Button size="sm" variant="outline" className="h-8 text-[11.5px] text-emerald-700 border-emerald-200" onClick={() => save({ status: "finalised" })} disabled={saving} data-testid="payroll-finalise-btn">
              <LockSimple size={12} className="mr-1" /> Finalise
            </Button>
          )}
          <Button size="sm" className="h-8 text-[11.5px] bg-[#0F172A] text-white hover:bg-slate-800" onClick={() => save({ days_in_month: run.days_in_month, pay_date: run.pay_date || "" })} disabled={!dirty || saving || locked} data-testid="payroll-save-btn">
            <FloppyDisk size={12} className="mr-1" /> {saving ? "Saving…" : dirty ? "Save changes" : "Saved"}
          </Button>
        </div>

        <div className="mt-3 border border-slate-200 rounded-sm overflow-x-auto">
          <Table className="text-[11px]">
            <TableHeader>
              <TableRow>
                <TableHead className="w-8">#</TableHead>
                <TableHead className="min-w-[190px]">Employee</TableHead>
                <TableHead className="text-center w-16">Days</TableHead>
                <TableHead className="text-right w-24">Basic</TableHead>
                <TableHead className="text-right w-24">HRA</TableHead>
                <TableHead className="text-right w-24">Conv</TableHead>
                <TableHead className="text-right w-24">Other +</TableHead>
                <TableHead className="text-right w-28">Gross</TableHead>
                <TableHead className="text-right w-24">PF Wages</TableHead>
                <TableHead className="text-right w-24">PF</TableHead>
                <TableHead className="text-right w-24">ESI Wages</TableHead>
                <TableHead className="text-right w-20">ESI</TableHead>
                <TableHead className="text-right w-24">Other −</TableHead>
                <TableHead className="text-right w-28">Att. Ded</TableHead>
                <TableHead className="text-right w-28">Net</TableHead>
                <TableHead className="w-28">Slip</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map(({ r, idx }) => {
                const d = derive(r);
                return (
                  <TableRow key={idx} className="hover:bg-slate-50" data-testid={`payroll-row-${idx}`}>
                    <TableCell className="font-mono-num text-slate-500">{r.sno}</TableCell>
                    <TableCell>
                      <div className="font-semibold text-slate-900 leading-tight">{r.name}</div>
                      <div className="text-[10px] text-slate-500 font-mono-num">{r.emp_code || "—"} · UAN {r.uan || "—"}{r.designation ? ` · ${r.designation}` : ""}</div>
                    </TableCell>
                    <TableCell><Cell r={r} k="days_paid" idx={idx} set={setField} locked={locked} center /></TableCell>
                    <TableCell><Cell r={r} k="basic" idx={idx} set={setField} locked={locked} hint={r.fixed_basic} /></TableCell>
                    <TableCell><Cell r={r} k="hra" idx={idx} set={setField} locked={locked} hint={r.fixed_hra} /></TableCell>
                    <TableCell><Cell r={r} k="conv" idx={idx} set={setField} locked={locked} hint={r.fixed_conv} /></TableCell>
                    <TableCell><Cell r={r} k="other_earnings" idx={idx} set={setField} locked={locked} /></TableCell>
                    <TableCell className="text-right font-mono-num font-semibold">{fmtMoney(d.gross)}</TableCell>
                    <TableCell><Cell r={r} k="pf_wages" idx={idx} set={setField} locked={locked} /></TableCell>
                    <TableCell><Cell r={r} k="pf_employee" idx={idx} set={setField} locked={locked} /></TableCell>
                    <TableCell><Cell r={r} k="esi_wages" idx={idx} set={setField} locked={locked} /></TableCell>
                    <TableCell><Cell r={r} k="esi_employee" idx={idx} set={setField} locked={locked} /></TableCell>
                    <TableCell><Cell r={r} k="other_deductions" idx={idx} set={setField} locked={locked} /></TableCell>
                    <TableCell>
                      <Cell r={r} k="attendance_deduction" idx={idx} set={setField} locked={locked} />
                      {(n(r.leaves) > 0 || n(r.late_marks) > 0 || n(r.half_days) > 0) && (
                        <div className="text-[9px] text-amber-700 font-mono-num text-right mt-0.5" data-testid={`payroll-row-attendance-${idx}`}>Lv {n(r.leaves)} · Late {n(r.late_marks)} · HD {n(r.half_days)}</div>
                      )}
                    </TableCell>
                    <TableCell className={`text-right font-mono-num font-bold ${d.net < 0 ? "text-rose-700" : "text-slate-900"}`} data-testid={`payroll-row-net-${idx}`}>{fmtMoney(d.net)}</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button size="sm" variant="outline" className="h-7 w-7 p-0 rounded-sm" title="Preview slip" onClick={() => setPreview({ idx, row: r })} data-testid={`payroll-preview-${idx}`}>
                          <Eye size={12} />
                        </Button>
                        <Button size="sm" variant="outline" className="h-7 rounded-sm text-[10.5px] px-2" title="Download slip PDF"
                                onClick={() => downloadWithToast(`/payroll/runs/${runId}/slips/${idx}/pdf`, slipName(r)).catch(() => {})} data-testid={`payroll-slip-pdf-${idx}`}>
                          <FilePdf size={12} className="mr-0.5" /> PDF
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
        {dirty && !locked && <div className="mt-2 text-[11px] text-amber-700">Unsaved changes — slips always render from the last saved values.</div>}

        {preview && <SlipPreviewDialog runId={runId} idx={preview.idx} row={preview.row} filename={slipName(preview.row)} onClose={() => setPreview(null)} />}
      </SheetContent>
    </Sheet>
  );
}

function Meta({ label, children }) {
  return (
    <div className="border border-slate-200 bg-white rounded-sm px-2.5 py-1.5">
      <div className="text-[9px] uppercase tracking-[0.14em] font-semibold text-slate-500">{label}</div>
      <div className="mt-0.5">{children}</div>
    </div>
  );
}

function Cell({ r, k, idx, set, locked, hint, center }) {
  const changed = hint !== undefined && n(hint) !== n(r[k]);
  return (
    <Input type="number" step="1" value={r[k] ?? ""} disabled={locked}
           title={hint !== undefined ? `Monthly rate ${fmtMoney(hint)}` : undefined}
           onChange={(e) => set(idx, k, e.target.value)}
           className={`h-7 px-1.5 text-[11px] font-mono-num ${center ? "text-center w-14" : "text-right w-full"} ${changed ? "border-amber-300 bg-amber-50/60" : ""}`}
           data-testid={`payroll-cell-${k}-${idx}`} />
  );
}
